import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, OWNER_EMAIL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sparkles, AlertTriangle, ShieldAlert, Loader2, Database, Trash2, FileText, Bell, Send, Briefcase, Wrench,
} from "lucide-react";

const CONFIRM_TEXT = "RESET DEFINITIVO";

export default function DemoTools() {
  const { user } = useAuth();
  const isOwner = user?.email && user.email.trim().toLowerCase() === OWNER_EMAIL;
  const [hotels, setHotels] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(true);

  // Seed demo state
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [seedOpen, setSeedOpen] = useState(false);

  // Reset state
  const [keepIds, setKeepIds] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Test push state
  const [users, setUsers] = useState([]);
  const [pushUserId, setPushUserId] = useState("");
  const [pushTitle, setPushTitle] = useState("IH AuditFlow — test push");
  const [pushBody, setPushBody] = useState("Se vedi questa notifica, le push funzionano correttamente 👍");
  const [pushSending, setPushSending] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  // Maintenance state (owner only)
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  useEffect(() => {
    if (!isOwner) return;
    api.get("/maintenance/status").then((r) => setMaintenance(r.data)).catch(() => {});
  }, [isOwner]);

  const toggleMaintenance = async (enable) => {
    if (enable) {
      const ok = window.confirm(
        "Confermi? Tutti gli altri utenti verranno disconnessi dalla prossima richiesta."
      );
      if (!ok) return;
    }
    setMaintenanceBusy(true);
    try {
      const { data } = enable
        ? await api.post("/maintenance/enable", { message: maintenanceMsg || undefined })
        : await api.post("/maintenance/disable");
      setMaintenance(data);
      toast.success(enable ? "Modalità manutenzione ATTIVATA" : "Modalità manutenzione DISATTIVATA");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMaintenanceBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/hotels");
        setHotels(data || []);
        // Preselect Joy124 / Smart if present
        const pre = (data || []).filter((h) =>
          /joy\s*124|smart/i.test(h.nome || "")
        ).map((h) => h.id);
        setKeepIds(pre);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoadingHotels(false);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/users?archived=false");
        setUsers((data || []).filter((u) => u.attivo));
      } catch { /* noop */ }
    })();
  }, []);

  const sendTestPush = async () => {
    if (!pushUserId) { toast.error("Seleziona un utente"); return; }
    setPushSending(true);
    setPushResult(null);
    try {
      const { data } = await api.post("/push/test", {
        user_id: pushUserId, title: pushTitle, body: pushBody, url: "/",
      });
      setPushResult(data);
      const nDev = data.total_subscriptions || 0;
      if ((data.sent || 0) > 0) {
        toast.success(`Push inviata a ${data.sent}/${nDev} dispositivi`);
      } else if (nDev === 0) {
        toast.error("L'utente non ha dispositivi iscritti alle push");
      } else {
        toast.error(`Nessuna push consegnata (${data.failed || 0} fallite · ${data.purged || 0} rimosse)`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPushSending(false);
    }
  };

  const canReset = confirmText === CONFIRM_TEXT && !resetting;

  const runSeedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo", { scenario: "presentation" });
      setSeedResult(data);
      toast.success(
        `Seed demo completato — ${Object.values(data.created).reduce((a, b) => a + b, 0)} record creati`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
      setSeedOpen(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/reset-selective/preview", { keep_hotel_ids: keepIds });
      setPreview(data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const runReset = async () => {
    if (!canReset) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-selective", {
        keep_hotel_ids: keepIds,
        confirm_text: CONFIRM_TEXT,
      });
      setResetResult(data);
      toast.success("Reset selettivo completato");
      setPreviewOpen(false);
      setConfirmText("");
      // Reload the page in 1.5s to reflect the wiped state
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  const toggleKeep = (id) => {
    setKeepIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const previewCounters = useMemo(() => preview?.counters || {}, [preview]);
  const totalToDelete = useMemo(() => {
    const keys = [
      "monthly_reports_v2", "daily_checks", "staff_reviews", "staff_stars",
      "user_absences", "user_daily_assignments", "notifications",
      "hotel_yearly_targets", "audits", "housekeeping_productivity",
      "hotels_delete", "hotel_rooms_delete", "staff_members_delete",
      "users_non_admin_delete",
    ];
    return keys.reduce((s, k) => s + (previewCounters[k] || 0), 0);
  }, [previewCounters]);

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="demo-tools-page">
      <header className="space-y-1">
        <div className="text-[10px] uppercase tracking-[0.16em] text-lavender-700 dark:text-lavender-300 font-semibold">
          Configurazione · Demo &amp; Reset
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Strumenti pre-presentazione
        </h1>
        <p className="text-muted-foreground text-sm max-w-2xl">
          Prepara la piattaforma per una demo con dati verosimili oppure ripulisci l'ambiente
          conservando solo le strutture selezionate. Entrambe le azioni sono riservate al Super Admin.
        </p>
      </header>

      {/* WEEKLY REPORT TRIGGER */}
      <section className="ih-glass rounded-2xl p-6 border-l-4 border-l-lavender-400" data-testid="weekly-trigger-section">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-[280px] flex-1">
            <div className="flex items-center gap-2 text-lavender-700 dark:text-lavender-300 mb-1">
              <FileText className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Weekly Report · Test manuale</span>
            </div>
            <h2 className="font-display text-xl font-semibold mb-2">Genera report settimana corrente</h2>
            <p className="text-sm text-foreground/80">
              Genera adesso il report della settimana ISO più recente completata (lun-dom scorsi). Il job automatico gira ogni lunedì alle 08:00 Europe/Rome.
            </p>
            <p className="text-xs text-muted-foreground mt-2">Se un report della stessa settimana esiste già, viene sovrascritto.</p>
          </div>
          <button
            data-testid="trigger-weekly-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/weekly-reports/generate?overwrite=true");
                toast.success(`Report ${data.week_iso} generato — ${data.hotels} hotel · media ${data.corporate_avg ?? "—"}/100`);
              } catch (e) {
                toast.error("Errore generazione report");
              }
            }}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-lavender-500 hover:bg-lavender-600 text-white shadow-md"
          >
            <FileText className="h-4 w-4" />
            Genera settimana corrente
          </button>
        </div>
      </section>

      {/* EXECUTIVE REPORT PDF */}
      <section
        className="ih-glass rounded-2xl p-6 border-l-4 border-l-lavender-500"
        data-testid="valutazione-section"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-[280px] flex-1">
            <div className="flex items-center gap-2 text-lavender-700 dark:text-lavender-300 mb-1">
              <FileText className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Executive Report</span>
            </div>
            <h2 className="font-display text-xl font-semibold mb-2">
              Valutazione economica IH AuditFlow
            </h2>
            <ul className="text-sm text-foreground/80 space-y-1 list-disc pl-5">
              <li>Executive summary con valore complessivo e payback stimato</li>
              <li>Cost value: dettaglio ore/ruolo per sviluppo equivalente</li>
              <li>Beneficio annuo diretto + alternative SaaS evitate</li>
              <li>Run-cost hosting + AI + manutenzione + piano evolutivo</li>
              <li>Conclusioni e raccomandazione strategica</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-3">
              PDF A4 6 sezioni · palette corporate lavanda · pronto per condivisione board.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 min-w-[220px]">
            <button
              onClick={async () => {
                try {
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/valutazione-app/pdf`,
                    { credentials: "include" }
                  );
                  if (!res.ok) throw new Error("Download error");
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "IH_AuditFlow_Valutazione_Economica.pdf";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success("Valutazione economica scaricata");
                } catch (e) {
                  toast.error("Errore nel download del PDF");
                }
              }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-lavender-500 hover:bg-lavender-600 text-white shadow-md"
              data-testid="download-valutazione-btn"
            >
              <FileText className="h-4 w-4" />
              Scarica valutazione PDF
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/valutazione-agenzia/pdf`,
                    { credentials: "include" }
                  );
                  if (!res.ok) throw new Error("Download error");
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "IH_AuditFlow_Valutazione_Commerciale.pdf";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success("Valutazione commerciale scaricata");
                } catch (e) {
                  toast.error("Errore nel download del PDF");
                }
              }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white shadow-md"
              data-testid="download-valutazione-agenzia-btn"
              title="PDF con stima costo di sviluppo agenzia e range di vendita commerciale"
            >
              <Briefcase className="h-4 w-4" />
              Genera Valutazione Commerciale
            </button>
            <p className="text-[10px] text-muted-foreground text-right max-w-[220px] leading-snug">
              Stima costo di sviluppo agenzia + range di vendita commerciale (SA only)
            </p>
          </div>
        </div>
      </section>

      {/* TEST WEB PUSH */}
      <section
        className="ih-glass rounded-2xl p-6 border-l-4 border-l-emerald-500"
        data-testid="push-test-section"
      >
        <div className="flex items-start gap-6 flex-wrap">
          <div className="min-w-[280px] flex-1">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 mb-1">
              <Bell className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Notifiche Push</span>
            </div>
            <h2 className="font-display text-xl font-semibold mb-2">Invia push di test</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Utile per validare la configurazione VAPID e mostrare live la notifica su un dispositivo iscritto.
              L'utente deve aver già attivato le push dal proprio Profilo.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Utente destinatario</Label>
                <select
                  value={pushUserId}
                  onChange={(e) => setPushUserId(e.target.value)}
                  data-testid="push-test-user"
                  className="w-full h-9 rounded-md border border-input bg-background/60 px-3 text-sm"
                >
                  <option value="">— seleziona —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome} {u.cognome} · {u.role} · {u.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Titolo</Label>
                <Input value={pushTitle} onChange={(e) => setPushTitle(e.target.value)} data-testid="push-test-title" />
              </div>
              <div className="space-y-1.5">
                <Label>Corpo</Label>
                <Input value={pushBody} onChange={(e) => setPushBody(e.target.value)} data-testid="push-test-body" />
              </div>
            </div>
            <div className="mt-4">
              <Button data-testid="push-test-send" onClick={sendTestPush} disabled={pushSending || !pushUserId} className="gap-2">
                {pushSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Invia push di test
              </Button>
            </div>
            {pushResult && (
              <div className="mt-4 text-xs rounded-lg border border-border/60 bg-background/40 p-3" data-testid="push-test-result">
                Dispositivi iscritti: <strong>{pushResult.total_subscriptions ?? 0}</strong> · Inviate: <strong className="text-emerald-600">{pushResult.sent ?? 0}</strong> · Fallite: <strong className="text-rose-600">{pushResult.failed ?? 0}</strong> · Rimosse: <strong>{pushResult.purged ?? 0}</strong>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MODALITÀ MANUTENZIONE — visibile SOLO all'owner */}
      {isOwner && (
        <section
          className="ih-glass rounded-2xl p-6 border-l-4 border-l-lavender-500"
          data-testid="maintenance-section"
        >
          <div className="flex items-start gap-6 flex-wrap">
            <div className="min-w-[280px] flex-1">
              <div className="flex items-center gap-2 text-lavender-700 dark:text-lavender-300 mb-1">
                <Wrench className="h-4 w-4" />
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Owner Only</span>
              </div>
              <h2 className="font-display text-xl font-semibold mb-2">Modalità Manutenzione</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Metti l'app offline per tutti gli utenti tranne te. Utile per migrazioni DB, deploy critici, demo private.
                Le sessioni attive di altri utenti riceveranno 503 alla prossima request.
              </p>
              {maintenance?.maintenance_mode ? (
                <div className="space-y-3">
                  <div className="rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 px-4 py-3 text-sm" data-testid="maintenance-active-badge">
                    <div className="font-semibold flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                      Manutenzione ATTIVA dal {maintenance.started_at ? new Date(maintenance.started_at).toLocaleString("it-IT") : "—"}
                    </div>
                    <div className="text-xs mt-1">Messaggio: "{maintenance.message}"</div>
                  </div>
                  <Button
                    data-testid="maintenance-disable-btn"
                    onClick={() => toggleMaintenance(false)}
                    disabled={maintenanceBusy}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {maintenanceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                    Disattiva Manutenzione
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Messaggio personalizzato (opzionale)</Label>
                    <Input
                      value={maintenanceMsg}
                      onChange={(e) => setMaintenanceMsg(e.target.value)}
                      placeholder="Sistema in manutenzione. Torneremo online a breve."
                      data-testid="maintenance-message-input"
                    />
                  </div>
                  <Button
                    data-testid="maintenance-enable-btn"
                    onClick={() => toggleMaintenance(true)}
                    disabled={maintenanceBusy || maintenance === null}
                    className="gap-2 bg-rose-600 hover:bg-rose-700"
                  >
                    {maintenanceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                    Attiva Manutenzione
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* SEED DEMO */}
      <section
        className="ih-glass rounded-2xl p-6 border-l-4 border-l-lavender-400"
        data-testid="seed-demo-section"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-[280px] flex-1">
            <div className="flex items-center gap-2 text-lavender-700 dark:text-lavender-300 mb-1">
              <Sparkles className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Seed demo</span>
            </div>
            <h2 className="font-display text-xl font-semibold mb-2">
              Popola dati demo per presentazione
            </h2>
            <ul className="text-sm text-foreground/80 space-y-1 list-disc pl-5">
              <li>3–4 relazioni mensili V2 inviate su hotel realistici (mese corrente + precedente)</li>
              <li>Daily checks completati oggi con valutazioni miste</li>
              <li>Produttività HK settimanali per mostrare colori (2.1 / 2.5 / 2.8)</li>
              <li>1–2 staff members con review + stelle mensili</li>
              <li>Obiettivi annuali {new Date().getFullYear()}</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-3">
              Idempotente: richiamare più volte NON crea duplicati. Non tocca utenti, hotel o camere esistenti.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 min-w-[220px]">
            <Button
              onClick={() => setSeedOpen(true)}
              disabled={seeding}
              className="bg-lavender-500 hover:bg-lavender-600 text-white"
              data-testid="seed-demo-btn"
            >
              {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
              Popola dati demo
            </Button>
            {seedResult && (
              <div className="text-xs text-muted-foreground text-right" data-testid="seed-result">
                Creati: relazioni {seedResult.created.relazioni} · check {seedResult.created.checks} ·
                HK {seedResult.created.hk_productivity} · staff {seedResult.created.staff} ·
                target {seedResult.created.targets}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* RESET SELETTIVO */}
      <section
        className="ih-glass rounded-2xl p-6 border-l-4 border-l-rose-500"
        data-testid="reset-section"
      >
        <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 mb-1">
          <ShieldAlert className="h-4 w-4" />
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Reset selettivo</span>
        </div>
        <h2 className="font-display text-xl font-semibold mb-3">
          Ripulisci ambiente conservando solo hotel selezionati
        </h2>

        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 px-4 py-3 mb-5 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-rose-800 dark:text-rose-200">
            <strong>Azione TOTALMENTE DISTRUTTIVA.</strong> Verranno cancellati: tutte le relazioni,
            daily checks, staff review, stelle, assenze, DLA, notifiche, obiettivi annuali, audit 60gg,
            produttività HK, gli hotel non selezionati (con cascade su camere e staff) e tutti gli
            utenti non-super_admin. Usa sempre l'anteprima prima di procedere.
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Hotel da preservare</Label>
            {loadingHotels ? (
              <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 inline animate-spin mr-1" /> Caricamento…</div>
            ) : hotels.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nessun hotel disponibile.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="hotels-keep-list">
                {hotels.map((h) => (
                  <label
                    key={h.id}
                    className="flex items-center gap-3 rounded-xl border border-border/60 bg-background/40 px-3 py-2 hover:bg-accent/40 cursor-pointer"
                  >
                    <Checkbox
                      checked={keepIds.includes(h.id)}
                      onCheckedChange={() => toggleKeep(h.id)}
                      data-testid={`keep-hotel-${h.id}`}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{h.nome}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {h.citta} · {h.numero_camere} camere
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Selezionati: <strong>{keepIds.length}</strong> · Verranno cancellati:{" "}
              <strong>{hotels.length - keepIds.length}</strong>
            </p>
          </div>

          <div>
            <Label htmlFor="confirm-text" className="mb-2 block">
              Digita <code className="bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 px-1.5 rounded text-xs">{CONFIRM_TEXT}</code> per confermare
            </Label>
            <Input
              id="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_TEXT}
              className="max-w-md"
              data-testid="confirm-text-input"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={previewing}
              data-testid="preview-btn"
            >
              {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
              Anteprima (dry-run)
            </Button>
            <Button
              disabled={!canReset}
              onClick={() => (preview ? setPreviewOpen(true) : runPreview())}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              data-testid="reset-btn"
            >
              {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Esegui reset
            </Button>
            {!canReset && confirmText.length > 0 && (
              <span className="text-xs text-rose-500">Il testo di conferma non combacia esattamente.</span>
            )}
          </div>
        </div>

        {resetResult && (
          <div className="mt-5 text-xs text-muted-foreground" data-testid="reset-result">
            Totale record cancellati:{" "}
            <strong>
              {Object.values(resetResult.deleted).reduce((a, b) => a + b, 0)}
            </strong>
          </div>
        )}
      </section>

      {/* SEED CONFIRM DIALOG */}
      <Dialog open={seedOpen} onOpenChange={setSeedOpen}>
        <DialogContent data-testid="seed-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confermi il seed demo?</DialogTitle>
            <DialogDescription>
              L'operazione popola dati verosimili sui primi 4 hotel attivi. È idempotente
              (nessun duplicato) e NON modifica utenti, hotel o camere esistenti.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedOpen(false)}>Annulla</Button>
            <Button
              onClick={runSeedDemo}
              disabled={seeding}
              className="bg-lavender-500 hover:bg-lavender-600 text-white"
              data-testid="seed-confirm-btn"
            >
              {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Sì, procedi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PREVIEW / RESET CONFIRM DIALOG */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-500" />
              Anteprima reset selettivo
            </DialogTitle>
            <DialogDescription>
              Verifica cosa verrà cancellato e cosa preservato prima di confermare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Preservati</div>
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-3 text-sm">
                <div>Hotel preservati: <strong>{previewCounters.hotels_preserved || 0}</strong></div>
                <div>Super Admin: <strong>{previewCounters.users_super_admin_preserved || 0}</strong></div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Verranno cancellati ({totalToDelete} record totali)
              </div>
              <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-3 text-sm space-y-0.5 font-mono">
                {[
                  ["Relazioni V2", "monthly_reports_v2"],
                  ["Daily checks", "daily_checks"],
                  ["Staff review", "staff_reviews"],
                  ["Stelle staff", "staff_stars"],
                  ["Assenze", "user_absences"],
                  ["DLA assignments", "user_daily_assignments"],
                  ["Notifiche", "notifications"],
                  ["Target annuali", "hotel_yearly_targets"],
                  ["Audit 60gg", "audits"],
                  ["Produttività HK", "housekeeping_productivity"],
                  ["Hotel cancellati", "hotels_delete"],
                  ["Camere (cascade)", "hotel_rooms_delete"],
                  ["Staff (cascade)", "staff_members_delete"],
                  ["Utenti non-admin", "users_non_admin_delete"],
                ].map(([label, key]) => (
                  <div key={key} className="flex justify-between">
                    <span>{label}</span>
                    <strong>{previewCounters[key] ?? 0}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Annulla</Button>
            <Button
              onClick={runReset}
              disabled={!canReset}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              data-testid="reset-confirm-btn"
            >
              {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Conferma reset definitivo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
